import { Form, Formik, FormikHelpers } from "formik";
import { useState } from "react";
import { opretStamdataaendringInteresse } from "../../api/stamdata";
import { ApiRequestError } from "../../api/util";
import { PersonStamdata } from "../../interfaces/stamdata";
import { PersonStamdataAendringInteresseForm } from "../../interfaces/stamdata";
import { hasPendingStamdataSection } from "../../lib/stamdataPendingChange";
import { accountInteresserValidationSchema } from "../../util/validationSchema/accountInteresser";
import { AccordionBody, AccordionHeader, AccordionItem } from "../Accordion";
import { Button } from "../Button";
import InteresseField from "../form/InteresseField";
import Spinner from "../graphics/Spinner";

const getSubmitErrorMessage = (error: unknown) => {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error) return error.message;
  return "Der opstod en fejl. Prøv igen senere.";
};

const toInteresseFormValues = (
  interesser: { id: string; navn: string }[] = [],
): PersonStamdataAendringInteresseForm => ({
  interesse1: interesser[0] ?? null,
  interesse2: interesser[1] ?? null,
  interesse3: interesser[2] ?? null,
});

const toInteresseIds = (values: PersonStamdataAendringInteresseForm) =>
  [values.interesse1, values.interesse2, values.interesse3]
    .filter((interesse) => interesse?.id)
    .map((interesse) => interesse!.id);

const hasInteresseSelection = (values: PersonStamdataAendringInteresseForm) =>
  toInteresseIds(values).length > 0;

const AccountInteresser = ({
  stamdata,
}: {
  stamdata: PersonStamdata | null;
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const hasSectionPendingChange =
    stamdata != null && hasPendingStamdataSection(stamdata, "interesser");
  const showPendingAlert = hasSectionPendingChange && !submitSuccess;
  const isFormLocked = hasSectionPendingChange || submitSuccess;
  const initialValues = toInteresseFormValues(stamdata?.interesser ?? []);

  const handleSubmit = async (
    values: PersonStamdataAendringInteresseForm,
    { resetForm }: FormikHelpers<PersonStamdataAendringInteresseForm>,
  ) => {
    if (!stamdata || isFormLocked) return;

    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      await opretStamdataaendringInteresse({
        personId: stamdata.id,
        interesseIds: toInteresseIds(values),
      });
      resetForm({ values });
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(getSubmitErrorMessage(error));
    }
  };

  return (
    <AccordionItem value="interesser">
      <AccordionHeader>Interesseområder</AccordionHeader>
      <AccordionBody>
        {!stamdata ? (
          <div className="alert alert-danger mb-0" role="alert">
            Kunne ikke hente dine oplysninger. Prøv at genindlæse siden.
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={accountInteresserValidationSchema}
            validateOnChange
            validateOnBlur
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ dirty, isSubmitting, errors, submitCount, values }) => {
              const canSubmit = dirty && hasInteresseSelection(values);
              const validationError =
                typeof errors === "string"
                  ? errors
                  : submitCount > 0
                    ? Object.values(errors)[0]
                    : null;

              return (
                <Form>
                  {showPendingAlert ? (
                    <div className="alert alert-info mb-4" role="status">
                      Du har allerede en aktiv anmodning om ændring af dine
                      oplysninger. Den behandles af Advokatsamfundet.
                    </div>
                  ) : null}

                  {submitError ? (
                    <div
                      className="alert alert-danger d-flex align-items-start justify-content-between gap-3 mb-4"
                      role="alert"
                    >
                      <span>{submitError}</span>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Luk fejlmeddelelse"
                        onClick={() => setSubmitError(null)}
                      />
                    </div>
                  ) : null}

                  {submitSuccess ? (
                    <div
                      className="alert alert-success d-flex align-items-start justify-content-between gap-3 mb-4"
                      role="alert"
                    >
                      <span>Din anmodning om ændring er sendt.</span>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Luk bekræftelse"
                        onClick={() => setSubmitSuccess(false)}
                      />
                    </div>
                  ) : null}

                  {validationError ? (
                    <div className="alert alert-danger mb-4" role="alert">
                      {validationError}
                    </div>
                  ) : null}

                  <fieldset disabled={isFormLocked}>
                    <div className="row">
                      <div className="col-6">
                        <legend className="fs-5 fw-semibold mb-4">
                          Vælg op til 3 interesseområder
                        </legend>
                        <div className="vstack gap-3">
                          <div>
                            <InteresseField
                              name="interesse1"
                              placeholder="Interesseområde 1"
                            />
                          </div>
                          <div>
                            <InteresseField
                              name="interesse2"
                              placeholder="Interesseområde 2"
                            />
                          </div>
                          <div>
                            <InteresseField
                              name="interesse3"
                              placeholder="Interesseområde 3"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                  <div className="d-flex justify-content-end">
                    <Button
                      id="account-interesser-submit"
                      disabled={!canSubmit || isFormLocked || isSubmitting}
                      className="btn btn-lg btn-primary mt-4"
                      type="submit"
                      aria-label="Gem interesseområder"
                    >
                      {isSubmitting ? (
                        <span className="d-inline-flex align-items-center gap-2">
                          <Spinner inline size={16} label="" />
                          Gemmer...
                        </span>
                      ) : (
                        "Gem"
                      )}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </AccordionBody>
    </AccordionItem>
  );
};

export default AccountInteresser;
